import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  PortalCoverage,
  PortalMetric,
  PortalSeriesPoint,
} from '../../../../types/portalReport';
import {
  connectableSegments,
  coverageNote,
  isSpendWithoutLeads,
} from '../../../../lib/portalReport/series';
import { formatReportingAmount } from '../../../../lib/reportingMoney';
import { ReferenceDot } from 'recharts';

// CLIENT PORTAL Phase 1 — the report chart.
//
// ⚠️⚠️ THIS COMPONENT IS WHERE "NO DATA" BECOMES VISIBLE, and the whole design
// turns on it. A month we never measured must not be a bar of height zero, which
// to a business owner reads as "your advertising produced nothing". So:
//
//   - its value is NULL, so recharts draws no bar at all;
//   - a shaded REFERENCE BAND covers the unmeasured span, so the absence is
//     something you can see rather than something you have to notice;
//   - the band is labelled, and a sentence under the chart explains it;
//   - the tooltip on such a bucket says "no data", never a figure.
//
// ⚠️ The CURRENT month is plotted but drawn in a distinct in-progress fill with
// a "so far" tooltip — a partial bar sitting beside twelve complete ones
// otherwise reads as a collapse in performance.
//
// ⚠️ ANIMATION IS OFF. The report re-renders on every metric switch, grouping
// switch, campaign-filter change and period change, and replaying a grow-in
// animation on each of those is distracting rather than delightful on something
// someone is reading numbers off. It also makes the rules above genuinely
// testable: with animation on, recharts emits EMPTY bar elements in jsdom, so a
// gap and a zero render identically and any assertion between them would pass
// for both.

const FILL = {
  complete: 'hsl(152 60% 36%)',
  /** Lighter: real, but the bucket is not finished. */
  'in-progress': 'hsl(152 45% 68%)',
  /** Amber: real, but only part of the bucket (or of the locations) was measured. */
  partial: 'hsl(38 85% 62%)',
  /** Never drawn — the value is null. Present so the map is exhaustive. */
  'no-data': 'transparent',
} as const;

export interface ReportChartProps {
  series: PortalSeriesPoint[];
  metric: PortalMetric;
  metricLabel: string;
  /**
   * What this brand calls a lead, for the spent-no-leads marker's hover.
   * ⚠️ Passed rather than derived from `metricLabel`: a gym's CPL label is
   * "Cost per member", and inferring "members" back out of it by string
   * surgery would break the moment a brand words it differently.
   */
  leadsLabel: string;
  currency: string | null;
  coverage: PortalCoverage;
  height?: number;
  /** Bars read better for months; a line reads better for a long daily period. */
  shape?: 'bar' | 'line';
}

function valueOf(point: PortalSeriesPoint, metric: PortalMetric): number | null {
  if (metric === 'leads') return point.leads;
  if (metric === 'spend') return point.spend;
  return point.costPerLead;
}

export interface ChartRow {
  bucket: string;
  label: string;
  coverage: PortalSeriesPoint['coverage'];
  value: number | null;
  /**
   * ⚠️ Round 2, item 5. True when this bucket is MEASURED, spent money, and
   * returned no leads — so its null CPL is a KNOWN state rather than an absence
   * of data. The chart connects its line across these and marks the point; it
   * still never plots a figure, because the only figure available would be 0
   * and 0 reads as "leads were free".
   */
  spendWithoutLeads: boolean;
  /** The spend that bought nothing, for the marker's hover. */
  spendAtPoint: number | null;
}

/**
 * The rows handed to recharts.
 *
 * ⚠️ EXPORTED SO THE NULL CAN BE ASSERTED. This is the exact line a `?? 0` would
 * be written on, and it is the last place a gap can be turned back into a zero
 * before it reaches the screen. It cannot be checked through the rendered DOM:
 * recharts animates bars in, and jsdom never fires the animation frames, so the
 * bar elements exist but are empty either way — a DOM-level assertion there
 * would pass for null AND for 0, which is exactly the distinction that matters.
 */
export function chartRows(
  series: readonly PortalSeriesPoint[],
  metric: PortalMetric,
): ChartRow[] {
  return series.map((point) => ({
    bucket: point.bucket,
    label: point.label,
    coverage: point.coverage,
    value: valueOf(point, metric),
    // Only meaningful on the CPL series — leads and spend have real figures for
    // such a bucket (0 and the spend), so there is no hole to explain there.
    spendWithoutLeads: metric === 'costPerLead' && isSpendWithoutLeads(point),
    spendAtPoint: point.spend,
  }));
}

/** The recharts dataKey for one connectable segment. */
export function segmentKey(index: number): string {
  return `seg${index}`;
}

function formatValue(
  value: number | null,
  metric: PortalMetric,
  currency: string | null,
): string {
  if (value == null) return '—';
  if (metric === 'leads') return value.toLocaleString('en-US', { maximumFractionDigits: 1 });
  return formatReportingAmount(value, currency ?? 'AUD');
}

/** Contiguous runs of unmeasured buckets, so one band covers each gap. */
export function gapBands(
  series: readonly PortalSeriesPoint[],
): Array<{ from: string; to: string }> {
  const bands: Array<{ from: string; to: string }> = [];
  let start: string | null = null;
  let previous: string | null = null;
  for (const point of series) {
    if (point.coverage === 'no-data') {
      if (start === null) start = point.bucket;
      previous = point.bucket;
    } else if (start !== null && previous !== null) {
      bands.push({ from: start, to: previous });
      start = null;
      previous = null;
    }
  }
  if (start !== null && previous !== null) bands.push({ from: start, to: previous });
  return bands;
}

export function ReportChart({
  series,
  metric,
  metricLabel,
  leadsLabel,
  currency,
  coverage,
  height = 220,
  shape = 'bar',
}: ReportChartProps) {
  const baseRows = chartRows(series, metric);
  // ⚠️ Per-segment keys so recharts draws one line per connectable run. A row
  // carries a value ONLY under its own segment's key; every other key is null,
  // which is what keeps the segments from being joined to each other.
  const segments = connectableSegments(series);
  const rows = baseRows.map((row, i) => {
    const withKeys: Record<string, unknown> = { ...row };
    segments.forEach((segment, si) => {
      withKeys[segmentKey(si)] = i >= segment.from && i <= segment.to ? row.value : null;
    });
    return withKeys as ChartRow & Record<string, unknown>;
  });

  const bands = gapBands(series);
  const note = coverageNote(series, coverage);
  const hasAnyValue = rows.some((r) => r.value != null);

  const tooltip = (
    <Tooltip
      cursor={{ fill: 'hsl(210 40% 96%)' }}
      content={({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const row = payload[0]?.payload as ChartRow | undefined;
        if (!row) return null;
        return (
          <div className="rounded-md border border-border bg-card px-3 py-2 text-sm shadow-sm">
            <div className="font-medium text-foreground">{row.label}</div>
            {row.coverage === 'no-data' ? (
              // ⚠️ Never a figure here. "No data" is the answer, and saying it in
              // the tooltip is what stops a reader inferring a zero.
              <div className="mt-0.5 text-muted-foreground">No data for this period</div>
            ) : (
              <div className="mt-0.5 text-foreground">
                {row.spendWithoutLeads ? (
                  // ⚠️ States BOTH facts — money out, nothing back. This is the
                  // one place the reader can find out what actually happened,
                  // so it must not be coy about it.
                  <>
                    Spent{' '}
                    {formatReportingAmount(row.spendAtPoint ?? 0, currency ?? 'AUD')} · 0{' '}
                    {leadsLabel.toLowerCase()}
                  </>
                ) : (
                  <>
                    {metricLabel}: {formatValue(row.value, metric, currency)}
                  </>
                )}
                {row.coverage === 'in-progress' && (
                  <span className="ml-1 text-muted-foreground">(so far)</span>
                )}
                {row.coverage === 'partial' && (
                  <span className="ml-1 text-amber-600">(partial)</span>
                )}
              </div>
            )}
          </div>
        );
      }}
    />
  );

  const axes = [
    <CartesianGrid key="grid" strokeDasharray="3 3" vertical={false} stroke="hsl(210 30% 92%)" />,
    <XAxis
      key="x"
      dataKey="label"
      tickLine={false}
      axisLine={false}
      tick={{ fontSize: 11, fill: 'hsl(215 16% 47%)' }}
      interval="preserveStartEnd"
      minTickGap={16}
    />,
    <YAxis
      key="y"
      tickLine={false}
      axisLine={false}
      width={52}
      tick={{ fontSize: 11, fill: 'hsl(215 16% 47%)' }}
      tickFormatter={(v: number) => formatValue(v, metric, currency)}
    />,
    tooltip,
    // ⚠️ The visible gap. Rendered BEFORE the series so the bars sit on top.
    ...bands.map((band) => (
      <ReferenceArea
        key={`gap-${band.from}`}
        x1={series.find((p) => p.bucket === band.from)?.label}
        x2={series.find((p) => p.bucket === band.to)?.label}
        fill="hsl(210 30% 90%)"
        fillOpacity={0.55}
        label={{ value: 'No data', position: 'insideTop', fontSize: 10, fill: 'hsl(215 16% 47%)' }}
      />
    )),
  ];

  return (
    <div>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          {shape === 'line' ? (
            <LineChart data={rows} margin={{ top: 12, right: 8, bottom: 0, left: 0 }}>
              {axes}
              {/*
                ⚠️⚠️ ONE LINE PER SEGMENT, and that is what gives this chart two
                different behaviours for two different nulls. A single
                `connectNulls` boolean is all-or-nothing across every null in the
                series, so it cannot both break at unmeasured days AND join
                across a day we KNOW spent money and got no leads.

                Each segment is a run with no unmeasured buckets in it, drawn
                with connectNulls TRUE — so a spent-no-leads hole inside a
                segment joins up (it stopped looking like a rendering bug), while
                the space BETWEEN segments has no line at all, because we know
                nothing about those days and drawing through them would be a
                confident claim about nothing.
              */}
              {segments.map((segment, i) => (
                <Line
                  key={`seg-${segment.from}`}
                  type="monotone"
                  dataKey={segmentKey(i)}
                  stroke={FILL.complete}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              ))}
              {/*
                ⚠️ The spent-no-leads marker. A hollow dot ON THE AXIS, not a
                plotted value: there is no CPL to plot, and the only number
                available (0) would read as "leads were free" when the truth is
                money spent for nothing. The tooltip carries the real facts.
              */}
              {rows.map((row) =>
                row.spendWithoutLeads ? (
                  <ReferenceDot
                    key={`nolead-${row.bucket}`}
                    x={row.label}
                    y={0}
                    r={4}
                    fill="#ffffff"
                    stroke={FILL.partial}
                    strokeWidth={2}
                    ifOverflow="extendDomain"
                  />
                ) : null,
              )}
            </LineChart>
          ) : (
            <BarChart data={rows} margin={{ top: 12, right: 8, bottom: 0, left: 0 }}>
              {axes}
              <Bar dataKey="value" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                {rows.map((row) => (
                  <Cell key={row.bucket} fill={FILL[row.coverage]} />
                ))}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      {!hasAnyValue && (
        <p className="mt-2 text-sm text-muted-foreground">
          No {metricLabel.toLowerCase()} to show for this period.
        </p>
      )}

      <ChartLegend series={series} metric={metric} leadsLabel={leadsLabel} />

      {note && <p className="mt-2 max-w-prose text-xs leading-relaxed text-muted-foreground">{note}</p>}
    </div>
  );
}

/** Only shows the states actually present, so a clean chart carries no clutter. */
function ChartLegend({
  series,
  metric,
  leadsLabel,
}: {
  series: readonly PortalSeriesPoint[];
  metric: PortalMetric;
  leadsLabel: string;
}) {
  const present = new Set(series.map((p) => p.coverage));
  const items: Array<{ key: string; swatch: string; label: string }> = [];
  // ⚠️ Only on the CPL series — leads and spend have real figures for such a
  // bucket, so there is nothing to explain there.
  if (metric === 'costPerLead' && series.some(isSpendWithoutLeads)) {
    items.push({
      key: 'no-leads',
      swatch: FILL.partial,
      label: `Spend, no ${leadsLabel.toLowerCase()}`,
    });
  }
  if (present.has('in-progress')) {
    items.push({ key: 'in-progress', swatch: FILL['in-progress'], label: 'In progress' });
  }
  if (present.has('partial')) {
    items.push({ key: 'partial', swatch: FILL.partial, label: 'Partial month' });
  }
  if (present.has('no-data')) {
    items.push({ key: 'no-data', swatch: 'hsl(210 30% 90%)', label: 'No data' });
  }
  if (items.length === 0) return null;
  return (
    <div
      data-testid="chart-legend"
      className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground"
    >
      {items.map((item) => (
        <span key={item.key} className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: item.swatch }}
            aria-hidden
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}
